import type { UseFormReturn } from 'react-hook-form';

import dayjs from 'dayjs';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import type { Client, Contact } from '~/api/clients/types';

type FormWithClientContact = {
  clientId: number | null;
  condition: { id: number; type: 'condition' | 'customAgreement' | 'customCondition' } | null;
  contactId: number | null;
  specialConditions: string;
};

/**
 * Selecting or removing a client/contact keeps the copyright `condition` and `specialConditions`
 * in sync: a contact with a custom agreement (raamovereenkomst) locks both to that agreement.
 * Shared between the client picker dialog and the selected-client card so the behaviour stays
 * consistent across every document type.
 */
export const useClientContactSelection = <T extends FormWithClientContact>(
  form: T extends FormWithClientContact ? UseFormReturn<T> : never,
) => {
  const {
    formState: { isSubmitted },
    setValue,
  } = form;
  const { t } = useTranslation(['invoices']);

  // After a failed submit these fields may be showing a required error, and `setValue` on its own
  // leaves it on screen — the new value has to be validated for the error to clear.
  const shouldValidate = isSubmitted;

  const applyCustomAgreement = useCallback(
    (contact: Contact) => {
      const [customAgreement] = contact.customAgreements ?? [];

      if (!customAgreement) return;

      setValue('condition', { id: customAgreement.id, type: 'customAgreement' }, { shouldValidate });
      setValue(
        'specialConditions',
        t('invoices:fields.specialConditions.custom', {
          date: dayjs(customAgreement.date).format('DD-MM-YYYY'),
          lng: contact.language,
        }),
      );
    },
    [setValue, shouldValidate, t],
  );

  const selectClientContact = useCallback(
    (client: Client, contact: Contact) => {
      setValue('clientId', client.id, { shouldValidate });
      setValue('contactId', contact.id, { shouldValidate });
      applyCustomAgreement(contact);
    },
    [applyCustomAgreement, setValue, shouldValidate],
  );

  const clearClientContact = useCallback(
    (contact: Contact | null) => {
      setValue('clientId', null, { shouldValidate });
      setValue('contactId', null, { shouldValidate });

      // Only wipe the condition/special conditions when they were auto-set by a custom agreement.
      if ((contact?.customAgreements ?? []).length > 0) {
        setValue('condition', null, { shouldValidate });
        setValue('specialConditions', '');
      }
    },
    [setValue, shouldValidate],
  );

  return { clearClientContact, selectClientContact };
};
